/**
 * PURPOSE: Orchestrator — full-screen photo annotation overlay with canvas + toolbar
 */
import { Check, X } from 'lucide-react'
import type { PhotoAnnotationProps } from './types'
import { useAnnotation } from './useAnnotation'
import { Toolbar } from './Toolbar'

export default function PhotoAnnotation({ imageUrl, onSave, onCancel }: PhotoAnnotationProps) {
  const {
    canvasRef, containerRef, imageLoaded,
    activeTool, setActiveTool, activeColor, setActiveColor,
    annotations, redoStack, textInput, setTextInput, textPosition, setTextPosition,
    handlePointerDown, handlePointerMove, handlePointerUp,
    handleTextSubmit, handleUndo, handleRedo, handleClearAll, handleSave,
  } = useAnnotation(imageUrl, onSave)

  if (!imageLoaded) return <div className="fixed inset-0 z-50 bg-black flex items-center justify-center"><div className="text-white text-sm">Loading image...</div></div>

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <header className="flex-shrink-0 bg-black/90 px-3 py-2 flex items-center justify-between">
        <button onClick={onCancel} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"><X className="w-5 h-5 text-white" /></button>
        <span className="text-white text-sm font-medium">Annotate Photo</span>
        <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 flex items-center gap-1.5 transition-colors"><Check className="w-4 h-4" />Done</button>
      </header>
      <div ref={containerRef} className="flex-1 flex items-center justify-center overflow-hidden bg-black">
        <canvas ref={canvasRef} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} className="touch-none cursor-crosshair" style={{ maxWidth: '100%', maxHeight: '100%' }} />
      </div>
      {textPosition && (
        <div className="absolute inset-x-0 top-1/3 flex justify-center z-50 px-4">
          <div className="bg-card border border-border rounded-xl p-4 shadow-2xl w-full max-w-sm">
            <input type="text" value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder="Enter label text..." autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              onKeyDown={(e) => { if (e.key === 'Enter') handleTextSubmit(); if (e.key === 'Escape') { setTextPosition(null); setTextInput('') } }} />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setTextPosition(null); setTextInput('') }} className="flex-1 py-2 rounded-lg bg-muted text-foreground text-sm font-medium">Cancel</button>
              <button onClick={handleTextSubmit} disabled={!textInput.trim()} className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">Add Label</button>
            </div>
          </div>
        </div>
      )}
      <Toolbar activeTool={activeTool} onToolChange={setActiveTool} activeColor={activeColor} onColorChange={setActiveColor} canUndo={annotations.length > 0} canRedo={redoStack.length > 0} canClear={annotations.length > 0} onUndo={handleUndo} onRedo={handleRedo} onClear={handleClearAll} />
    </div>
  )
}
